import './ExchangeEur.sass';

import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';

import {
  Block,
  Button,
  Form,
  FormItem,
  Header,
  Input,
  Placeholder,
  ScrollView,
} from '$uikit';
import { Api } from '$api';
import { Loader } from '$shared/components';
import { useTranslator } from '$hooks';
import { toastActions } from '$store/toast';
import { formatInputAmount } from '$utils/number';
import { batchActions } from '$store';
import { mainActions } from '$store/main';
import { goBack } from '$navigation/helper';
import { CurrencyEnum } from '$store/models';

export const ExchangeEur: FC = () => {
  const dispatch = useDispatch();
  const t = useTranslator();

  const [amount, setAmount] = useState('');
  const [loadingStatus, setLoadingStatus] = useState('loading');
  const [tonRate, setTonRate] = useState(0);

  const loadRate = useCallback(() => {
    setLoadingStatus('loading');
    Api.get('/rates/convert/ton')
      .then((resp) => {
        const data: any = resp;
        setTonRate(data.rate);
        setLoadingStatus('');
      })
      .catch((err) => {
        dispatch(toastActions.setToastFail(err.message));
        setLoadingStatus('failed');
      });
  }, []);

  useEffect(() => {
    loadRate();
  }, []);

  const handleSend = useCallback(() => {
    dispatch(toastActions.setToastLoading());
    Api.post('/balance/eur/convert', {
      amount: +amount,
    })
      .then((resp) => {
        const data: any = resp;
        dispatch(
          batchActions(
            mainActions.setBalance(data.balance),
            mainActions.setEurBalance(data.eurBalance),
            toastActions.hideToast(),
          ),
        );
        goBack();
      })
      .catch((err) => {
        dispatch(toastActions.setToastFail(err.message));
      });
  }, [amount]);

  const amountTip = useMemo(() => {
    if (+amount <= 0) {
      return t('exchange_eur_amount_err');
    }

    return t('exchange_eur_amount_caption', {
      amount: formatInputAmount(`${+amount / tonRate}`, 2),
    });
  }, [tonRate, amount]);

  const handleAmountChange = useCallback((e) => {
    setAmount(formatInputAmount(e.target.value));
  }, []);

  function renderContent() {
    if (loadingStatus === 'loading') {
      return <Loader isWrapped />;
    }

    if (loadingStatus === 'failed' || tonRate <= 0) {
      return (
        <Placeholder
          title={t('failed_title')}
          actions={
            <Button size="small" onClick={loadRate}>
              {t('failed_button')}
            </Button>
          }
        >
          {t('failed_caption')}
        </Placeholder>
      );
    }

    return (
      <ScrollView>
        <div className="ExchangeEur__message_wrap">
          <Block>
            <div className="ExchangeEur__message">{t('exchange_eur_caption')}</div>
          </Block>
        </div>
        <Form>
          <FormItem title={t('exchange_eur_amount')} description={amountTip}>
            <Input
              type="price"
              currency={CurrencyEnum.EUR}
              placeholder={t('exchange_eur_amount_placeholder')}
              value={amount}
              onChange={handleAmountChange}
            />
          </FormItem>
          <FormItem>
            <Button onClick={handleSend} disabled={+amount < 1}>
              {t('exchange_eur_button')}
            </Button>
          </FormItem>
        </Form>
      </ScrollView>
    );
  }

  return (
    <>
      <Header showBackButton>{t('exchange_eur_title')}</Header>
      {renderContent()}
    </>
  );
};
